import { Stack, StackProps } from '@smartfm/ui/components/Stack';
import { AdvancedBuildingSelector } from '@smartfm/react-components';
import { useDrawerContext } from '@/components/layouts/DrawerProvider';
import { Typography } from '@smartfm/ui/components/Typography';
import { IconButton } from '@smartfm/ui/components/IconButton';
import { Button } from '@smartfm/ui/components/Button';
import { Divider } from '@smartfm/ui/components/Divider';
import { useState, useMemo, useCallback, memo } from 'react';
import { useAdvancedNavigationTree } from '@smartfm/container/hooks/useAdvancedNavigationTree';
import { useFavoritesContext } from '@smartfm/container/context/favorites';
import { Banner } from '@smartfm/ui/components/Banner/Banner';
import { Box } from '@smartfm/ui/components/Box';

const maxPartialSelectionCount = 1000;
interface HeaderProps {
  isPartialSelectionLimitReached: boolean;
  onClose(): void;
}

const Header = memo(function Header(props: HeaderProps) {
  const { onClose, isPartialSelectionLimitReached } = props;

  return (
    <Stack
      direction='column'
      sx={{
        padding: theme => theme.spacing(1, 3, 0),
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
      >
        <Typography variant='Calibre/H6 Headline'>Select Buildings</Typography>
        <IconButton
          type='Icon'
          shape='Circle'
          iconFillType='rounded'
          size='Large'
          title='Close'
          icon='close'
          onClick={onClose}
        />
      </Stack>
      {isPartialSelectionLimitReached && (
        <Box sx={{ marginBottom: 1 }}>
          <Banner
            isDismissable={false}
            variant='warning'
            content={
              <>
                Please limit your selection to &quot;
                <u>{maxPartialSelectionCount} Buildings</u>&quot; or select
                &quot;
                <u>All Buildings</u>&quot;.
              </>
            }
          />
        </Box>
      )}
    </Stack>
  );
});

interface ContentProps {
  children: StackProps['children'];
}

const Content = memo(function Content(props: ContentProps) {
  return (
    <Stack
      sx={{
        padding: theme => theme.spacing(0, 3),
        flexGrow: 1,
        minHeight: 0,
      }}
      {...props}
    />
  );
});

interface FooterProps {
  selectedItemsCount: number;
  isPartialSelectionLimitReached: boolean;
  onCancel(): void;
  onApply(): void;
}

const Footer = memo(function Footer(props: FooterProps) {
  const {
    selectedItemsCount,
    isPartialSelectionLimitReached,
    onCancel,
    onApply,
  } = props;

  return (
    <Stack>
      <Divider />
      <Stack
        direction='row'
        spacing={2}
        useFlexGap={true}
        justifyContent='space-between'
        alignItems='center'
        sx={{ paddingX: 3, paddingY: 1, minHeight: 56 }}
      >
        <Typography
          variant='Calibre/Body 2'
          sx={{ color: 'text.secondary' }}
        >
          {selectedItemsCount} Selected
        </Typography>

        <Stack
          direction='row'
          spacing={2}
          useFlexGap={true}
        >
          <Button
            type='Secondary'
            label='Cancel'
            onClick={onCancel}
          />
          <Button
            label='Apply'
            disabled={
              selectedItemsCount === 0 || isPartialSelectionLimitReached
            }
            onClick={onApply}
          />
        </Stack>
      </Stack>
    </Stack>
  );
});

interface ConnectedAdvancedBuildingSelectorDrawerContentProps {
  values: string[];
  clientId: string;
  onChange?(ids: string[]): void;
}

export const ConnectedAdvancedBuildingSelectorDrawerContent = memo(
  function ConnectedAdvancedBuildingSelectorDrawerContent(
    props: ConnectedAdvancedBuildingSelectorDrawerContentProps
  ) {
    const { clientId, values, onChange } = props;
    const { setDrawerComponent } = useDrawerContext();
    const [selectedBuildingIds, setSelectedBuildingIds] = useState(values);

    const { data } = useAdvancedNavigationTree({
      clientId,
      enabled: Boolean(clientId),
    });

    // Memoized data from API response
    const treeItems = useMemo(
      () => (data?.tree ? [data.tree] : []),
      [data?.tree]
    );
    const allItems = useMemo(
      () => (data?.allItems ? data.allItems : []),
      [data?.allItems]
    );
    const itemsMap = useMemo(() => data?.itemsMap || {}, [data?.itemsMap]);
    const itemsOrder = useMemo(
      () => data?.itemsOrder || [],
      [data?.itemsOrder]
    );
    const totalItemsCount = useMemo(
      () => data?.totalItemsCount || 0,
      [data?.totalItemsCount]
    );

    const {
      favorites,
      toggleFavorite,
      isFavoriteFilterActive,
      toggleFavoriteFilter,
    } = useFavoritesContext();

    const handleCloseClick = useCallback(() => {
      setDrawerComponent(null);
    }, [setDrawerComponent]);

    const handleApplyClick = useCallback(() => {
      onChange?.(
        selectedBuildingIds.length === totalItemsCount
          ? []
          : selectedBuildingIds
      );
      setDrawerComponent(null);
    }, [onChange, selectedBuildingIds, setDrawerComponent, totalItemsCount]);

    const handleChange = useCallback((selectedItems: string[]) => {
      setSelectedBuildingIds(selectedItems);
    }, []);

    const selectedItemsCount = selectedBuildingIds.length;

    const isPartialSelectionLimitReached =
      selectedItemsCount > maxPartialSelectionCount &&
      selectedItemsCount < totalItemsCount;

    const parentToChildrenMap = useMemo(
      () => data?.parentToChildrenMap || {},
      [data?.parentToChildrenMap]
    );

    return (
      <Stack
        direction='column'
        sx={{
          height: '100%',
          maxHeight: '100%',
          width: 480,
        }}
      >
        <Stack
          direction='column'
          useFlexGap={true}
          spacing={1}
          sx={{ flexGrow: 1, minHeight: 0 }}
        >
          <Header
            onClose={handleCloseClick}
            isPartialSelectionLimitReached={isPartialSelectionLimitReached}
          />
          <Content>
            <AdvancedBuildingSelector
              totalItemsCount={totalItemsCount}
              allItems={allItems}
              treeItems={treeItems}
              buildingsOrder={itemsOrder}
              buildingsMap={itemsMap}
              selectedBuildings={selectedBuildingIds}
              onChange={handleChange}
              favorites={favorites}
              toggleFavorite={toggleFavorite}
              toggleFavoriteFilter={toggleFavoriteFilter}
              isFavoriteFilterActive={isFavoriteFilterActive}
              parentToChildrenMap={parentToChildrenMap}
            />
          </Content>
          <Footer
            isPartialSelectionLimitReached={isPartialSelectionLimitReached}
            selectedItemsCount={selectedItemsCount}
            onCancel={handleCloseClick}
            onApply={handleApplyClick}
          />
        </Stack>
      </Stack>
    );
  }
);
